import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import ContentPicker from './ContentPicker';
import {
    Upload, Image as ImageIcon, Film, Trash2, ExternalLink,
    Library, Link2, Play
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductMediaUploader({ productId, getAuthHeader }) {
    const [media, setMedia] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [showContentPicker, setShowContentPicker] = useState(false);
    const [showVideoDialog, setShowVideoDialog] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (productId && productId !== 'new') loadMedia();
    }, [productId]);

    const loadMedia = async () => {
        try {
            const res = await axios.get(`${API}/products/${productId}`, { headers: getAuthHeader() });
            setMedia(res.data.media || []);
        } catch {}
    };

    const uploadFile = async (file) => {
        if (!productId || productId === 'new') {
            return toast.error('Save the product first before uploading media');
        }

        const maxSize = 50 * 1024 * 1024;
        if (file.size > maxSize) {
            return toast.error('File too large. Maximum size is 50MB.');
        }

        setUploading(true);
        try {
            const { data: urlData } = await axios.post(
                `${API}/products/${productId}/upload-url`,
                { file_name: file.name, mime_type: file.type },
                { headers: getAuthHeader() },
            );

            await fetch(urlData.upload_url, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });

            const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
            await axios.post(
                `${API}/products/${productId}/media`,
                {
                    media_id: urlData.media_id,
                    type: mediaType,
                    s3_key: urlData.s3_key,
                    file_name: file.name,
                    mime_type: file.type,
                    file_size: file.size,
                },
                { headers: getAuthHeader() },
            );

            toast.success(`${file.name} uploaded`);
            loadMedia();
        } catch {
            toast.error(`Failed to upload ${file.name}`);
        } finally {
            setUploading(false);
        }
    };

    const handleFiles = (files) => {
        for (const file of files) {
            if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
                uploadFile(file);
            } else {
                toast.error(`${file.name} is not an image or video`);
            }
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
    };

    // Add media from Content Library selection
    const handleContentPickerSelect = async (asset) => {
        if (!productId || productId === 'new') {
            return toast.error('Save the product first');
        }
        try {
            await axios.post(
                `${API}/products/${productId}/media`,
                {
                    type: asset.type === 'video' ? 'video' : 'image',
                    s3_key: asset.s3_key || null,
                    external_url: asset.url || asset.external_url || null,
                    file_name: asset.name,
                    mime_type: asset.mime_type,
                    file_size: asset.file_size || 0,
                },
                { headers: getAuthHeader() },
            );
            toast.success(`Added ${asset.name} from Content Library`);
            loadMedia();
        } catch {
            toast.error('Failed to add from library');
        }
        setShowContentPicker(false);
    };

    // Add external video URL (YouTube, Loom, Vimeo, etc.)
    const handleAddVideoUrl = async () => {
        if (!videoUrl.trim()) return;
        if (!productId || productId === 'new') {
            return toast.error('Save the product first');
        }

        // Detect video provider
        let provider = null;
        const url = videoUrl.trim();
        if (url.includes('youtube.com') || url.includes('youtu.be')) provider = 'youtube';
        else if (url.includes('loom.com')) provider = 'loom';
        else if (url.includes('vimeo.com')) provider = 'vimeo';
        else if (url.includes('wistia.com')) provider = 'wistia';

        try {
            await axios.post(
                `${API}/products/${productId}/media`,
                {
                    type: 'video',
                    external_url: url,
                    file_name: provider ? `${provider} video` : 'External video',
                    caption: provider || '',
                },
                { headers: getAuthHeader() },
            );
            toast.success('Video added');
            setVideoUrl('');
            setShowVideoDialog(false);
            loadMedia();
        } catch {
            toast.error('Failed to add video');
        }
    };

    const handleDelete = async (mediaId) => {
        try {
            await axios.delete(`${API}/products/${productId}/media/${mediaId}`, { headers: getAuthHeader() });
            toast.success('Media removed');
            loadMedia();
        } catch {
            toast.error('Failed to remove media');
        }
    };

    // Generate video thumbnail or embed preview
    const getVideoThumbnail = (item) => {
        const url = item.external_url || '';
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
            const match = url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
            if (match) return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
        }
        return null;
    };

    return (
        <div className="space-y-3">
            {/* Action buttons row */}
            <div className="flex items-center gap-2">
                <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-stone-200 rounded-lg text-sm text-stone-600 hover:bg-stone-50 hover:border-stone-300 transition-colors"
                >
                    <Upload className="w-4 h-4" /> Upload File
                </button>
                <button
                    onClick={() => setShowContentPicker(true)}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-stone-200 rounded-lg text-sm text-stone-600 hover:bg-stone-50 hover:border-stone-300 transition-colors"
                >
                    <Library className="w-4 h-4" /> Content Library
                </button>
                <button
                    onClick={() => setShowVideoDialog(true)}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-stone-200 rounded-lg text-sm text-stone-600 hover:bg-stone-50 hover:border-stone-300 transition-colors"
                >
                    <Play className="w-4 h-4" /> Add Video URL
                </button>
            </div>

            {/* Drop zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center py-6 border-2 border-dashed rounded-xl transition-colors ${
                    dragOver ? 'border-blue-400 bg-blue-50' : 'border-stone-200 bg-stone-50/50'
                }`}
            >
                <Upload className={`w-6 h-6 mb-1.5 ${dragOver ? 'text-blue-500' : 'text-stone-300'}`} />
                <p className="text-xs text-stone-400">
                    {uploading ? 'Uploading...' : 'or drag and drop images & videos here'}
                </p>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
            />

            {/* Media grid */}
            {media.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                    {media.map(item => {
                        const videoThumb = item.type === 'video' ? getVideoThumbnail(item) : null;
                        const displayUrl = item.url || item.external_url;

                        return (
                            <div key={item.media_id} className="relative group rounded-lg overflow-hidden border border-stone-200 bg-stone-50">
                                {item.type === 'image' && item.url ? (
                                    <img
                                        src={item.url}
                                        alt={item.alt_text || item.file_name}
                                        className="w-full h-24 object-cover"
                                    />
                                ) : item.type === 'video' && videoThumb ? (
                                    <div className="relative w-full h-24">
                                        <img src={videoThumb} alt={item.file_name} className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-8 h-8 rounded-full bg-black/60 flex items-center justify-center">
                                                <Play className="w-4 h-4 text-white ml-0.5" />
                                            </div>
                                        </div>
                                    </div>
                                ) : item.type === 'video' ? (
                                    <div className="w-full h-24 flex flex-col items-center justify-center bg-purple-50">
                                        <Film className="w-6 h-6 text-purple-300" />
                                        {item.caption && <span className="text-[10px] text-purple-400 mt-1 capitalize">{item.caption}</span>}
                                    </div>
                                ) : (
                                    <div className="w-full h-24 flex items-center justify-center bg-stone-100">
                                        <ImageIcon className="w-6 h-6 text-stone-300" />
                                    </div>
                                )}

                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                    {displayUrl && (
                                        <a
                                            href={displayUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={e => e.stopPropagation()}
                                            className="p-1.5 bg-white/90 rounded-lg text-stone-700 hover:bg-white transition-colors"
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </a>
                                    )}
                                    <button
                                        onClick={() => handleDelete(item.media_id)}
                                        className="p-1.5 bg-white/90 rounded-lg text-red-500 hover:bg-white transition-colors"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                                <div className="px-2 py-1.5">
                                    <p className="text-[10px] text-stone-500 truncate">{item.file_name}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Content Library Picker */}
            <ContentPicker
                open={showContentPicker}
                onOpenChange={setShowContentPicker}
                onSelect={(asset) => handleContentPickerSelect(asset)}
                allowedTypes={['image', 'video']}
                mode="select"
            />

            {/* Add Video URL Dialog */}
            <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Video</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <p className="text-xs text-stone-500">
                            Paste a video URL from YouTube, Loom, Vimeo, or Wistia. The video will be embedded in your product page.
                        </p>
                        <Input
                            value={videoUrl}
                            onChange={e => setVideoUrl(e.target.value)}
                            placeholder="https://www.youtube.com/watch?v=..."
                            onKeyDown={e => e.key === 'Enter' && handleAddVideoUrl()}
                        />
                        <div className="flex flex-wrap gap-2">
                            {['YouTube', 'Loom', 'Vimeo', 'Wistia'].map(p => (
                                <span key={p} className="text-[10px] px-2 py-0.5 bg-stone-100 text-stone-400 rounded-full">{p}</span>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowVideoDialog(false)}>Cancel</Button>
                        <Button onClick={handleAddVideoUrl} disabled={!videoUrl.trim()} className="bg-stone-900 hover:bg-stone-800">
                            Add Video
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
